import { useVoiceRecording } from '@cotera/client/app/hooks';
import { cn } from '../lib/utils';
import { Loader2Icon } from 'lucide-react';
import { InputGroupButton } from './ui/input-group';
import React, { type ComponentProps } from 'react';
import { Icon } from '@cotera/client/app/components/ui/icon';

export type VoiceRecordingButtonProps = Omit<
  ComponentProps<typeof InputGroupButton>,
  'onClick'
> & {
  onTranscriptionComplete: (text: string) => void;
  onRecordingStateChange?: (recording: boolean) => void;
};

export const VoiceRecordingButton = ({
  className,
  onTranscriptionComplete,
  onRecordingStateChange,
  ...props
}: VoiceRecordingButtonProps) => {
  const { recording, transcribing, level, toggleRecording } = useVoiceRecording(
    onTranscriptionComplete
  );

  // Notify parent of recording state changes
  React.useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(recording);
    }
  }, [recording, onRecordingStateChange]);

  return (
    <InputGroupButton
      type="button"
      variant="ghost"
      size="icon-sm"
      className={cn(
        'relative transition-colors duration-200',
        recording && 'bg-primary/10 text-primary',
        transcribing && 'cursor-wait',
        className
      )}
      disabled={transcribing}
      onClick={toggleRecording}
      aria-label={
        recording
          ? 'Stop recording'
          : transcribing
          ? 'Transcribing...'
          : 'Start recording'
      }
      {...props}
    >
      {recording && (
        /*
          Scales with what the microphone is actually picking up, replacing a
          fixed `animate-pulse` that looked the same whether it was hearing
          speech or nothing at all. Sits behind the icon and ignores pointer
          events so it cannot interfere with the click target.
        */
        <span
          aria-hidden
          // `inherit` rather than a fixed radius: the button's corner comes from
          // its size variant, so hardcoding one here would drift the moment a
          // caller passed a different size.
          className="pointer-events-none absolute inset-0 rounded-[inherit] bg-primary/20 motion-reduce:hidden"
          style={{
            transform: `scale(${1 + level * 0.6})`,
            opacity: 0.35 + level * 0.45,
            // Fast enough to track speech, slow enough not to strobe.
            transition: 'transform 90ms linear, opacity 90ms linear',
          }}
        />
      )}
      {transcribing ? (
        <Loader2Icon className="size-4 animate-spin" />
      ) : (
        <Icon icon="audio" className="relative size-4" />
      )}
    </InputGroupButton>
  );
};
